import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Grid, Typography, Box, Paper, Button,
  Collapse, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Divider, IconButton, Tooltip, Chip, Snackbar, Alert
} from '@mui/material';
import {
  ExpandMore, ExpandLess, MusicNote, Lock, FiberManualRecord,
  Tune, Timer, ChecklistRtl, BarChart, InfoOutlined
} from '@mui/icons-material';
import PomodoroTimer, { PomodoroLog, TimerState } from '../../components/artists/PomodoroTimer';
import GoalList from '../../components/artists/GoalList';
import { saveToStorage, loadFromStorage, clearStorage } from '../../components/artists/storageUtils';

interface ViewPrefs {
  showStats: boolean;
  showTimer: boolean;
  showGoals: boolean;
}

const DEFAULT_PREFS: ViewPrefs = { showStats: true, showTimer: true, showGoals: true };

const PRESETS = [
  {
    value: 1,
    label: 'Quick session',
    detail: '1 Pomodoro',
    duration: '~30 min',
    description: 'One focused burst. Good for when you have limited time or just want to make a start.',
  },
  {
    value: 3,
    label: 'Half session',
    detail: '3 Pomodoros',
    duration: '~1.5 hrs',
    description: 'A solid amount of work with two proper breaks built in.',
  },
  {
    value: 5,
    label: 'Full session',
    detail: '5 Pomodoros',
    duration: '~2.5 hrs',
    description: 'The full Music Admin Mondays slot. A complete morning of focused work.',
  },
] as const;

function useLiveClock() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const msUntilNextMinute = (60 - new Date().getSeconds()) * 1000;
    const timeout = setTimeout(() => {
      setTime(new Date());
      const id = setInterval(() => setTime(new Date()), 60000);
      return () => clearInterval(id);
    }, msUntilNextMinute);
    return () => clearTimeout(timeout);
  }, []);
  return time;
}

function isSessionLive(now: Date): boolean {
  const day = now.getDay();
  const minuteOfDay = now.getHours() * 60 + now.getMinutes();
  return day === 1 && minuteOfDay >= 600 && minuteOfDay < 750;
}

function fmtDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return 'just started';
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, highlight }) => (
  <Box
    sx={{
      flex: 1, minWidth: 0, px: 2, py: 1.5, borderRadius: 2,
      bgcolor: highlight ? 'primary.main' : 'action.hover',
      border: '1px solid', borderColor: highlight ? 'primary.main' : 'divider',
      textAlign: 'center',
    }}
  >
    <Typography sx={{ fontFamily: 'monospace', fontSize: '1.5rem', fontWeight: 800, lineHeight: 1, color: highlight ? '#fff' : 'text.primary', letterSpacing: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: highlight ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.6rem' }}>
      {label}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: highlight ? 'rgba(255,255,255,0.65)' : 'text.disabled', fontSize: '0.65rem' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const MusicAdminPage: React.FC = () => {
  const [heroExpanded, setHeroExpanded] = useState<boolean>(false);
  const [clearDialogOpen, setClearDialogOpen] = useState<boolean>(false);
  const [explainerOpen, setExplainerOpen] = useState<boolean>(false);
  const [goalNudge, setGoalNudge] = useState<boolean>(false);
  const [logs, setLogs] = useState<PomodoroLog[]>([]);
  const [timerState, setTimerState] = useState<TimerState>({ isRunning: false, isWorkSession: true, sessionStartedAt: null });
  const [liveWorkSeconds, setLiveWorkSeconds] = useState<number>(0);
  const [sessionElapsed, setSessionElapsed] = useState<number>(0);
  const [customiseOpen, setCustomiseOpen] = useState<boolean>(false);
  const [sessionPreset, setSessionPreset] = useState<number | null>(() => loadFromStorage('sessionPreset'));
  const [goalCelebrated, setGoalCelebrated] = useState<boolean>(false);
  const [showGoalReached, setShowGoalReached] = useState<boolean>(false);
  const [prefs, setPrefs] = useState<ViewPrefs>(() => loadFromStorage('viewPrefs') ?? DEFAULT_PREFS);

  const sessionStartRef = useRef<Date | null>(null);
  const now = useLiveClock();
  const live = isSessionLive(now);

  const sessionHasStarted = logs.length > 0 || timerState.isRunning;

  useEffect(() => { saveToStorage('viewPrefs', prefs); }, [prefs]);
  useEffect(() => { saveToStorage('sessionPreset', sessionPreset); }, [sessionPreset]);

  useEffect(() => {
    if (logs.length > 0 && !sessionStartRef.current) {
      sessionStartRef.current = new Date(logs[0].startTime);
    }
  }, [logs]);

  useEffect(() => {
    const id = setInterval(() => {
      if (sessionStartRef.current) {
        setSessionElapsed(Math.floor((Date.now() - sessionStartRef.current.getTime()) / 1000));
      }
      if (timerState.isRunning && timerState.isWorkSession && timerState.sessionStartedAt) {
        setLiveWorkSeconds(Math.floor((Date.now() - new Date(timerState.sessionStartedAt).getTime()) / 1000));
      } else {
        setLiveWorkSeconds(0);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [timerState]);

  useEffect(() => {
    if (timerState.isRunning && timerState.isWorkSession && timerState.sessionStartedAt) {
      setLiveWorkSeconds(Math.floor((Date.now() - new Date(timerState.sessionStartedAt).getTime()) / 1000));
    } else {
      setLiveWorkSeconds(0);
    }
    if (!sessionStartRef.current && timerState.isRunning && timerState.sessionStartedAt) {
      sessionStartRef.current = new Date(timerState.sessionStartedAt);
    }
  }, [timerState]);

  const completedWorkSeconds = logs
    .filter(l => l.type === 'work')
    .reduce((acc, l) => acc + Math.round((new Date(l.endTime).getTime() - new Date(l.startTime).getTime()) / 1000), 0);
  const totalFocusSeconds = completedWorkSeconds + liveWorkSeconds;
  const focusSessions = logs.filter(l => l.type === 'work').length;

  // Fire goal-reached celebration once when preset target is hit
  useEffect(() => {
    if (sessionPreset && focusSessions >= sessionPreset && !goalCelebrated && focusSessions > 0) {
      setShowGoalReached(true);
      setGoalCelebrated(true);
    }
  }, [focusSessions, sessionPreset, goalCelebrated]);

  const handleClearConfirmed = () => {
    clearStorage('pomodoroState');
    clearStorage('goals');
    clearStorage('viewPrefs');
    clearStorage('sessionPreset');
    setClearDialogOpen(false);
    window.location.reload();
  };

  const handleSessionComplete = (type: 'work' | 'break') => {
    if (type === 'work') setGoalNudge(true);
  };

  const togglePref = (key: keyof ViewPrefs) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const clockString = now.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false });
  const visibleCount = [prefs.showTimer, prefs.showGoals].filter(Boolean).length;

  const sessionsStatValue = sessionPreset
    ? `${focusSessions}/${sessionPreset}`
    : focusSessions > 0 ? String(focusSessions) : '—';
  const sessionsStatSub = sessionPreset
    ? `${PRESETS.find(p => p.value === sessionPreset)?.label ?? ''}`
    : focusSessions === 1 ? '1 pomodoro' : focusSessions > 1 ? `${focusSessions} pomodoros` : undefined;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

      {/* Live session banner */}
      {live && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 2.5, py: 1.5, borderRadius: 2, bgcolor: 'success.main', color: '#fff' }}>
          <FiberManualRecord sx={{ fontSize: 12, animation: 'pulse 1.4s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
          <Typography variant="body2" fontWeight={600}>
            Music Admin Mondays is live right now — you're in session with others
          </Typography>
        </Box>
      )}

      {/* Hero card */}
      <Paper elevation={0} sx={{ mb: 3, border: '1.5px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          onClick={() => setHeroExpanded(e => !e)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MusicNote color="primary" />
            <Typography variant="h6" fontWeight={700}>Music Admin Mondays</Typography>
          </Box>
          <Tooltip title={heroExpanded ? 'Collapse' : 'About this session'}>
            <IconButton size="small">{heroExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Tooltip>
        </Box>
        <Collapse in={heroExpanded}>
          <Divider />
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, letterSpacing: 0.2, maxWidth: 600 }}>
              Feeling overwhelmed with admin tasks — booking gigs, social media, funding applications?
              These sessions give you a structured time to focus and get things done, alongside other musicians doing the same.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
              <strong>When:</strong> Mondays, 10:00 AM – 12:30 PM &nbsp;·&nbsp; <strong>Where:</strong> Online
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={e => { e.stopPropagation(); setClearDialogOpen(true); }}>
              Clear all session data
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Privacy notice */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3, px: 2, py: 1.5, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
        <Lock sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0, mt: 0.2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          <strong>Your data stays on your device.</strong> Your goals and timer are saved only in your browser's session storage — nothing is sent to any server, ever. Closing or refreshing this tab clears everything automatically. We never see it.{' '}
          <strong>Note:</strong> if you have this page open in multiple tabs, each tab works independently.
        </Typography>
      </Box>

      {/* Customise view */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button size="small" startIcon={<Tune fontSize="small" />} onClick={() => setCustomiseOpen(o => !o)}
          sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem' }}>
          Customise view
        </Button>
        <Collapse in={customiseOpen} orientation="horizontal" sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 1 }}>
            {([
              { key: 'showStats', icon: <BarChart fontSize="small" />, label: 'Stats' },
              { key: 'showTimer', icon: <Timer fontSize="small" />, label: 'Timer' },
              { key: 'showGoals', icon: <ChecklistRtl fontSize="small" />, label: 'Goals' },
            ] as const).map(({ key, icon, label }) => (
              <Chip key={key} icon={icon} label={label} size="small"
                variant={prefs[key] ? 'filled' : 'outlined'}
                color={prefs[key] ? 'primary' : 'default'}
                onClick={() => togglePref(key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Stats strip */}
      <Collapse in={prefs.showStats}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <StatCard label="Current time" value={clockString} highlight />
          <StatCard
            label="Focus time"
            value={totalFocusSeconds > 0 ? fmtDuration(totalFocusSeconds) : '—'}
            sub={timerState.isRunning && timerState.isWorkSession ? 'live — updating' : totalFocusSeconds > 0 ? 'this session' : 'not started yet'}
          />
          <StatCard
            label={sessionPreset ? 'Sessions done' : 'Sessions done'}
            value={sessionsStatValue}
            sub={sessionsStatSub}
          />
          <StatCard
            label="Time in session"
            value={sessionElapsed > 0 ? fmtDuration(sessionElapsed) : '—'}
            sub={sessionStartRef.current
              ? `started ${sessionStartRef.current.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false })}`
              : 'start the timer to begin'}
          />
        </Box>
      </Collapse>

      {/* ── Session preset picker — only shown before first session starts ── */}
      <Collapse in={!sessionHasStarted && prefs.showTimer}>
        <Paper elevation={0} sx={{ mb: 3, border: '1.5px solid', borderColor: 'divider', borderRadius: 3, p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            How long have you got today?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
            Pick a session length and we'll track your progress as you go. There's no pressure to stick to it — this is just to help you set an intention.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            {PRESETS.map(preset => (
              <Grid item xs={12} sm={4} key={preset.value}>
                <Paper
                  elevation={0}
                  onClick={() => setSessionPreset(preset.value)}
                  sx={{
                    p: 2, borderRadius: 2, cursor: 'pointer',
                    border: '2px solid',
                    borderColor: sessionPreset === preset.value ? 'primary.main' : 'divider',
                    bgcolor: sessionPreset === preset.value ? 'primary.50' : 'background.paper',
                    transition: 'border-color 0.2s, background 0.2s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>{preset.label}</Typography>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: 'block' }}>
                    {preset.detail} · {preset.duration}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                    {preset.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Button
            size="small"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
            onClick={() => setSessionPreset(null)}
          >
            I'll decide as I go — no preference
          </Button>
        </Paper>
      </Collapse>

      {/* If a preset is set and session has started, show a quiet reminder chip */}
      {sessionHasStarted && sessionPreset !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            label={`Goal: ${PRESETS.find(p => p.value === sessionPreset)?.label} (${focusSessions}/${sessionPreset} sessions)`}
            size="small"
            color={focusSessions >= sessionPreset ? 'success' : 'default'}
            onDelete={() => setSessionPreset(null)}
          />
        </Box>
      )}

      {/* Main widgets */}
      {visibleCount === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>All widgets are hidden.</Typography>
          <Button size="small" onClick={() => setCustomiseOpen(true)} startIcon={<Tune fontSize="small" />}>Customise view</Button>
        </Box>
      ) : (
        <Grid container spacing={3} alignItems="flex-start">
          {prefs.showTimer && (
            <Grid item xs={12} md={prefs.showGoals ? 6 : 12}>
              <Paper elevation={0} sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 3, p: 3 }}>
                {/* Timer header with explainer */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
                    Pomodoro Timer
                  </Typography>
                  <Tooltip title="What is a Pomodoro?">
                    <IconButton size="small" onClick={() => setExplainerOpen(true)} sx={{ color: 'text.secondary' }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <PomodoroTimer
                  onSessionComplete={handleSessionComplete}
                  onLogsChange={setLogs}
                  onTimerStateChange={setTimerState}
                />
              </Paper>
            </Grid>
          )}
          {prefs.showGoals && (
            <Grid item xs={12} md={prefs.showTimer ? 6 : 12}>
              <Paper elevation={0} sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 3, p: 3 }}>
                <GoalList nudgeOpen={goalNudge} onNudgeDismiss={() => setGoalNudge(false)} />
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── Pomodoro explainer dialog ── */}
      <Dialog open={explainerOpen} onClose={() => setExplainerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>What is a Pomodoro?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            The Pomodoro technique is a simple way to work in focused bursts without burning out.
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
            {[
              { step: 'Pick one thing to work on from your goals list.' },
              { step: 'Set the timer and work on just that thing until it rings. No switching, no checking your phone.' },
              { step: 'When the timer goes off, stop. Take a proper break — stand up, drink water, rest your eyes.' },
              { step: 'Repeat. After a few rounds, take a longer break.' },
            ].map((item, i) => (
              <Box component="li" key={i} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{item.step}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              <strong>Why does it work?</strong> It removes the pressure of open-ended work time. You're not working forever — just until the timer goes off. And knowing a break is coming makes it easier to stay focused.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplainerOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* ── Goal reached celebration ── */}
      <Snackbar
        open={showGoalReached}
        autoHideDuration={6000}
        onClose={() => setShowGoalReached(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowGoalReached(false)} sx={{ fontWeight: 600 }}>
          You've completed your {PRESETS.find(p => p.value === sessionPreset)?.label?.toLowerCase()} goal. That's the work done.
        </Alert>
      </Snackbar>

      {/* ── Confirm clear dialog ── */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Clear all session data?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ lineHeight: 1.7 }}>
            This will clear your Pomodoro log and all goals from your browser. This cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5, fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.7 }}>
            As a reminder: none of this data was ever stored on a server — it only ever existed in your browser.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClearConfirmed} color="error" variant="contained">Yes, clear everything</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MusicAdminPage;
